import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaPaperclip, FaTrashAlt, FaTimesCircle, FaCheck, FaCheckDouble, FaEllipsisV, FaFile, FaReply, FaTimes, FaHistory, FaPen } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { formatChatDate, formatChatTime, shouldShowDateSeparator } from "../../utils/dateUtils";
import io from "socket.io-client";
import ChatHistory from "../../components/ChatHistory";

const EDIT_WINDOW_MINUTES = 15;

const AdminChatbox = ({ user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [error, setError] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [typing, setTyping] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState("");
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const textareaRef = useRef(null);
  const sendingLock = useRef(false);
  const [chatboxSize, setChatboxSize] = useState({ width: 380, height: 500 });
  const resizeRef = useRef({ isResizing: false, startX: 0, startY: 0, startWidth: 0, startHeight: 0 });

  // ... existing code ...

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    const maxHeight = 120; // Same as maxHeight in style
    textarea.style.height = newHeight > maxHeight ? maxHeight + 'px' : newHeight + 'px';
    setNewMessage(textarea.value);
    
    // Emit typing event to server
    socketRef.current.emit('typing', {
      userId: user._id,
      isTyping: textarea.value.length > 0
    });
  };

  // Mark messages as read when chatbox opens
  const markMessagesAsRead = useCallback(async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user._id, userType: 'admin' }),
      });
    } catch (err) { console.error('Mark as read error:', err); }
  }, [user._id]);

  // Mark messages as delivered when admin opens chat
  const markMessagesAsDelivered = useCallback(async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-delivered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user._id, userType: 'admin' }),
      });
    } catch (err) { console.error('Mark as delivered error:', err); }
  }, [user._id]);

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_API_BASE_URL);
    socketRef.current.emit('joinRoom', 'admin');
    
    socketRef.current.on('newMessage', (message) => {
      if (message.userId === user._id) {
        setMessages(prevMessages => [...prevMessages, message]);
        // Auto-mark as read since admin has chat open
        markMessagesAsRead();
      }
    });
    
    socketRef.current.on('userTyping', ({ userId, isTyping }) => {
      if (userId === user._id) {
        setTyping(isTyping);
      }
    });

    // Listen for read receipts from user
    socketRef.current.on('messagesRead', ({ userId, readAt, readBy }) => {
      if (userId === user._id && readBy === 'user') {
        setMessages(prev => prev.map(msg =>
          msg.sender === 'admin' && !msg.readAt
            ? { ...msg, isRead: true, readAt }
            : msg
        ));
      }
    });

    // Listen for delivery receipts
    socketRef.current.on('messagesDelivered', ({ userId, deliveredAt }) => {
      if (userId === user._id) {
        setMessages(prev => prev.map(msg =>
          msg.sender === 'admin' && !msg.deliveredAt
            ? { ...msg, deliveredAt }
            : msg
        ));
      }
    });

    // Listen for message edits
    socketRef.current.on('messageEdited', ({ messageId, newMessage, isEdited, editedAt, userId }) => {
      if (userId === user._id) {
        setMessages(prev => prev.map(msg =>
          msg._id === messageId
            ? { ...msg, message: newMessage, isEdited, editedAt }
            : msg
        ));
      }
    });

    // Mark existing messages as read & delivered on open
    markMessagesAsRead();
    markMessagesAsDelivered();

    return () => {
      socketRef.current.disconnect();
    };
  }, [user._id, markMessagesAsRead, markMessagesAsDelivered]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen) {
        const headerActions = document.querySelector('.chatbox-header-actions');
        const menuDropdown = document.querySelector('.chatbox-menu-dropdown');

        // If click is outside both the header actions and the dropdown, close menu
        if (headerActions && menuDropdown &&
            !headerActions.contains(event.target) &&
            !menuDropdown.contains(event.target)) {
          setIsMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!user) return;

    const fetchChatHistory = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/history?userId=${user._id}&isAdmin=true`
        );
        if (!response.ok) throw new Error("Failed to fetch chat history.");
        const data = await response.json();
        setMessages(data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchChatHistory();

  }, [user]);

  const handleReply = (message) => {
    setReplyingTo(message);
    setNewMessage("");
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const scrollToMessage = (messageId) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight the message briefly
      messageElement.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  // Check if message is within edit window
  const canEditMessage = (msg) => {
    if (msg.sender !== 'admin' || msg.fileUrl || msg.pending || msg.failed) return false;
    if (msg._id?.startsWith('temp-')) return false;
    const timeDiff = (new Date() - new Date(msg.timestamp)) / 1000 / 60;
    return timeDiff <= EDIT_WINDOW_MINUTES;
  };

  // Start editing a message
  const startEditMessage = (msg) => {
    setEditingMessage(msg);
    setEditText(msg.message);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingMessage(null);
    setEditText("");
  };

  // Save edited message
  const saveEditMessage = async () => {
    if (!editingMessage || !editText.trim() || editText.trim() === editingMessage.message) {
      cancelEdit();
      return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/edit-message`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ messageId: editingMessage._id, newMessage: editText.trim() }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to edit message');
      }
      const updated = await response.json();
      setMessages(prev => prev.map(msg =>
        msg._id === updated._id ? { ...msg, message: updated.message, isEdited: true, editedAt: updated.editedAt } : msg
      ));
      cancelEdit();
    } catch (err) {
      setError(err.message);
    }
  };

  // Get tick icon for read receipts
  const getTickIcon = (msg) => {
    if (msg.sender !== 'admin') return null;
    if (msg.pending) return null;
    if (msg.failed) return null;

    if (msg.isRead || msg.readAt) {
      // Blue double tick = read
      return <FaCheckDouble className="w-3 h-3 text-blue-400" />;
    }
    if (msg.deliveredAt) {
      // Gray double tick = delivered
      return <FaCheckDouble className="w-3 h-3 text-white/60" />;
    }
    // Single gray tick = sent
    return <FaCheck className="w-3 h-3 text-white/60" />;
  };

  const handleMessageSelect = (messageId) => {
    if (selectedMessages.includes(messageId)) {
      setSelectedMessages(selectedMessages.filter((id) => id !== messageId));
    } else {
      setSelectedMessages([...selectedMessages, messageId]);
    }
  };

  const handleEnterSelectMode = () => {
    setSelectMode(true);
    setIsMenuOpen(false);
  };

  const handleExitSelectMode = () => {
    setSelectMode(false);
    setSelectedMessages([]);
    setIsMenuOpen(false);
  };

  const handleClose = () => {
    setSelectMode(false);
    setSelectedMessages([]);
    onClose();
  };

  // ... existing code ...

  const handleDeleteSelectedMessages = async () => {
    if (window.confirm("Are you sure you want to delete the selected messages?")) {
      const token = localStorage.getItem("adminToken");

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/delete-messages`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ messageIds: selectedMessages }),
          }
        );

        if (!response.ok) throw new Error("Failed to delete selected messages");

        setMessages(messages.filter((msg) => !selectedMessages.includes(msg._id)));
        setSelectedMessages([]);
        setSelectMode(false); // Exit select mode after deletion
        setIsMenuOpen(false);
      } catch (error) {
    setError(error.message);
      }
    }
  };

  const handleMessageSend = async () => {
    // Prevent multiple sends by checking lock
    if (sendingLock.current) return;
    
    if (newMessage.trim() !== "" || selectedFiles.length > 0) {
      // Set lock to prevent multiple sends
      sendingLock.current = true;
      setIsSending(true);
      setSendProgress(10); // Start progress
      
      const messageText = newMessage.trim();
      
      // Clear message input immediately
      setNewMessage("");
      
      try {
        if (selectedFiles.length > 0) {
          setSendProgress(30);
          await uploadFiles();
          setSendProgress(70);
          if (messageText !== "") {
            // Already handled by uploadFiles
          }
        } else {
          // Show message instantly, then send to server in background
          showInstantMessage(messageText);
          setSendProgress(50);
          await sendTextMessage(messageText);
        }
        setSendProgress(100);
        
        // Reset progress after completion
        setTimeout(() => {
          setIsSending(false);
          setSendProgress(0);
        }, 500);
      } catch (error) {
        setError("Failed to send message. Please try again.");
        setIsSending(false);
        setSendProgress(0);
      } finally {
        // Release lock after sending completes or fails
        sendingLock.current = false;
      }
    }
  };

  const showInstantMessage = (messageText) => {
    const instantMessage = {
      _id: `temp-${Date.now()}`,
      user: "Admin",
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      pending: true // Flag to style differently if needed
    };
    
    // Add message to UI immediately
    setMessages(prevMessages => [...prevMessages, instantMessage]);
    
    // Scroll to bottom immediately
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  };

  const sendTextMessage = async (messageText) => {
    const newMessageObj = {
      user: "Admin",
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      replyTo: replyingTo ? replyingTo._id : null, // Include replyTo if replying
    };

    const tempId = `temp-${Date.now()}`;
    
    try {
      // Message already displayed by showInstantMessage
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/send`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify(newMessageObj),
      });

      if (!response.ok) throw new Error("Failed to send message.");

      const data = await response.json();
      
      // Update temporary message with real server data
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-') && msg.message === messageText ? 
          { ...data, pending: false } : msg
        )
      );
      
      // Clear message input after successful send
      setNewMessage("");
      setReplyingTo(null); // Clear reply state after send
      
      // Reset textarea height properly
      if (textareaRef.current) {
        textareaRef.current.style.height = '35px'; // Set to min-height explicitly
      }
      
      // No need to set newMessage to empty here as we did it earlier
    } catch (error) {
      // Mark message as failed rather than removing it
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-') && msg.message === messageText ? 
          { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Emit typing event to server
    socketRef.current.emit('typing', {
      userId: user._id,
      isTyping: value.length > 0
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const removeSelectedFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem("adminToken");
    const formData = new FormData();
    
    // Add temporary file messages with pending status
    const tempMessages = selectedFiles.map(file => ({
      _id: `temp-file-${Date.now()}-${file.name}`,
      user: "Admin",
      message: `File uploaded: ${file.originalname || file.name}`,
      sender: "admin",
      timestamp: new Date().toISOString(),
      originalName: file.name,
      userId: user._id,
      pending: true
    }));
    
    // Show files as pending in UI
    setMessages(prevMessages => [...prevMessages, ...tempMessages]);
    
    selectedFiles.forEach(file => {
      formData.append("files", file);
    });
    
    formData.append("user", "Admin");
    formData.append("sender", "admin");
    formData.append("userId", user._id);
    formData.append("replyTo", replyingTo ? replyingTo._id : ""); // Include replyTo if replying
    if (newMessage.trim()) {
      formData.append("message", newMessage.trim());
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload files.");
      }

      const data = await response.json();
      
      // Remove temporary messages and add actual one
      setMessages(prevMessages => {
        const filteredMessages = prevMessages.filter(msg => !msg._id.startsWith('temp-file-'));
        return [...filteredMessages, data];
      });
      
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      // Mark temporary file messages as failed
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-file-') ? 
          { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    }
  };

  const handleMouseDown = (e) => {
    resizeRef.current.isResizing = true;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startWidth = chatboxSize.width;
    resizeRef.current.startHeight = chatboxSize.height;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!resizeRef.current.isResizing) return;

    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;

    // Invert deltaX to make left drag increase width and right drag decrease width
    const newWidth = Math.max(300, Math.min(800, resizeRef.current.startWidth - deltaX));
    // Invert deltaY to make up drag increase height and down drag decrease height
    const newHeight = Math.max(400, Math.min(800, resizeRef.current.startHeight - deltaY));

    setChatboxSize({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    resizeRef.current.isResizing = false;

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save size preference to localStorage
    localStorage.setItem('adminChatboxSize', JSON.stringify(chatboxSize));
  };

  // Load saved size preference on component mount
  useEffect(() => {
    const savedSize = localStorage.getItem('adminChatboxSize');
    if (savedSize) {
      try {
        const parsedSize = JSON.parse(savedSize);
        // Ensure constraints are respected
        const constrainedSize = {
          width: Math.max(300, Math.min(800, parsedSize.width || 380)),
          height: Math.max(400, Math.min(800, parsedSize.height || 500))
        };
        setChatboxSize(constrainedSize);
      } catch (error) {
        console.error('Error parsing saved admin chatbox size:', error);
      }
    }
  }, []);

  const truncateFileName = (name) => {
    if (name.length > 20) {
      return `${name.substring(0, 10)}...${name.substring(name.length - 7)}`;
    }
    return name;
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white dark:bg-dark-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-gray-200 dark:border-dark-700" style={{ width: chatboxSize.width, height: chatboxSize.height }}>
      {/* Header */}
      <div className={`flex items-center justify-between p-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white ${selectMode ? 'bg-gradient-to-r from-amber-500 to-amber-600' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold text-lg">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h5 className="font-semibold text-sm">{user.name}</h5>
            <p className="text-xs text-white/70">Active now</p>
          </div>
          {selectMode && (
            <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs">{selectedMessages.length} selected</span>
          )}
        </div>
        <div className="chatbox-header-actions flex items-center gap-1 relative">
          <button onClick={() => setShowHistory(true)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="View Chat History"><FaHistory /></button>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 hover:bg-white/10 rounded-lg transition-colors"><FaEllipsisV /></button>
          {isMenuOpen && (
            <div className="chatbox-menu-dropdown absolute top-full right-0 mt-1 w-48 bg-white dark:bg-dark-800 rounded-xl shadow-xl border border-gray-100 dark:border-dark-700 py-1 z-10">
              {!selectMode ? (
                <button onClick={handleEnterSelectMode} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"><FaCheck className="text-green-500" /> Select Messages</button>
              ) : (
                <>
                  {selectedMessages.length > 0 && (
                    <button onClick={handleDeleteSelectedMessages} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"><FaTrashAlt /> Delete ({selectedMessages.length})</button>
                  )}
                  <button onClick={handleExitSelectMode} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"><FaTimesCircle /> Exit Select</button>
                </>
              )}
              <button onClick={handleClose} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"><FaTimesCircle /> Close Chat</button>
            </div>
          )}
          <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-xl"><FaTimes /></button>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3 bg-gray-50 dark:bg-dark-900">
        {error && <div className="p-2 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg text-center">{error}</div>}
        
        {messages.map((msg, index) => {
          const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1]);
          const isAdmin = msg.sender === 'admin';
          const isEditingThis = editingMessage && editingMessage._id === msg._id;
          
          return (
            <React.Fragment key={index}>
              {showDateSeparator && (
                <div className="flex items-center justify-center my-4">
                  <span className="px-3 py-1 bg-gray-200 dark:bg-dark-700 text-gray-500 dark:text-gray-400 text-xs rounded-full">{formatChatDate(msg.timestamp)}</span>
                </div>
              )}
              <div id={`message-${msg._id}`} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} ${msg.pending ? 'opacity-60' : ''} ${msg.failed ? 'opacity-40' : ''}`} style={{ transition: 'background-color 0.3s ease' }}>
                <div className={`group relative max-w-[75%] ${selectMode ? 'flex items-start gap-2' : ''}`}>
                  {selectMode && !msg.pending && !msg.failed && (
                    <input type="checkbox" checked={selectedMessages.includes(msg._id)} onChange={() => handleMessageSelect(msg._id)} className="mt-2 w-4 h-4 text-primary-600 rounded" />
                  )}
                  <div className={`px-4 py-2 rounded-2xl ${isAdmin ? 'bg-primary-600 text-white rounded-br-md' : 'bg-white dark:bg-dark-800 text-gray-900 dark:text-white rounded-bl-md shadow-sm'}`}>
                    {msg.replyTo && (
                      <div 
                        onClick={() => scrollToMessage(msg.replyTo._id)}
                        className={`mb-2 p-2 rounded-lg text-xs border-l-2 cursor-pointer hover:opacity-80 transition-opacity ${
                        isAdmin ? 'bg-white/20 border-white/40' : 'bg-gray-100 dark:bg-dark-700 border-gray-400 dark:border-gray-500'
                      }`}>
                        <div className="flex items-center gap-1.5">
                          <span className={`font-medium ${
                            isAdmin ? 'text-white/90' : 'text-gray-700 dark:text-gray-300'
                          }`}>Replying to {msg.replyTo.user}:</span>
                        </div>
                        {msg.replyTo.fileUrl ? (
                          <div className="flex items-center gap-1.5 mt-1">
                            <FaFile className={`w-3 h-3 ${
                              isAdmin ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'
                            }`} />
                            <span className={`truncate ${
                              isAdmin ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {truncateFileName(msg.replyTo.originalName || 'File')}
                            </span>
                          </div>
                        ) : (
                          <p className={`mt-1 truncate ${
                            isAdmin ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {msg.replyTo.message?.substring(0, 50)}{msg.replyTo.message?.length > 50 ? '...' : ''}
                          </p>
                        )}
                      </div>
                    )}
                    {/* Message Content */}
                    {msg.message && (
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                    )}

                    {/* File Content */}
                    {(msg.fileUrl || (msg.files && msg.files.length > 0)) && (
                      <div className="flex flex-col gap-3 mt-2">
                        {msg.files && msg.files.length > 0 ? (
                          msg.files.map((file, fIdx) => (
                            <a 
                              key={fIdx}
                              href={`${process.env.REACT_APP_API_BASE_URL}${file.fileUrl}`} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                                isAdmin ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-800 dark:text-gray-200'
                              }`}
                            >
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                                isAdmin ? 'bg-white/20' : 'bg-white dark:bg-dark-800 shadow-sm'
                              }`}>
                                <FaFile className={isAdmin ? 'text-white' : 'text-blue-600'} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{file.originalName}</p>
                                <p className={`text-xs ${isAdmin ? 'text-white/60' : 'text-gray-500'}`}>Click to download</p>
                              </div>
                            </a>
                          ))
                        ) : (
                          <a 
                            href={`${process.env.REACT_APP_API_BASE_URL}${msg.fileUrl}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                              isAdmin ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-800 dark:text-gray-200'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                              isAdmin ? 'bg-white/20' : 'bg-white dark:bg-dark-800 shadow-sm'
                            }`}>
                              <FaFile className={isAdmin ? 'text-white' : 'text-blue-600'} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{msg.originalName}</p>
                              <p className={`text-xs ${isAdmin ? 'text-white/60' : 'text-gray-500'}`}>Click to download</p>
                            </div>
                          </a>
                        )}
                      </div>
                    )}
                    <div className={`flex items-center gap-1 mt-1 text-xs ${isAdmin ? 'text-white/60 justify-end' : 'text-gray-400'}`}>
                      {msg.isEdited && <span className="italic">edited</span>}
                      <span>{msg.pending ? 'Sending...' : msg.failed ? 'Failed' : formatChatTime(msg.timestamp)}</span>
                      {isAdmin && getTickIcon(msg)}
                    </div>
                  </div>
                  {/* Action buttons on hover */}
                  <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 ${isAdmin ? '-left-16' : '-right-16'}`}>
                    {isAdmin && canEditMessage(msg) && !isEditingThis && (
                      <button onClick={() => startEditMessage(msg)} className="p-1.5 rounded-full bg-gray-200 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300" title="Edit message"><FaPen className="w-2.5 h-2.5" /></button>
                    )}
                    <button onClick={() => handleReply(msg)} className="p-1.5 rounded-full bg-gray-200 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300" title="Reply"><FaReply className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        
        {typing && <div className="text-sm text-gray-500 dark:text-gray-400 italic">User is typing...</div>}
        
        {isSending && (
          <div className="h-1 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 transition-all duration-300" style={{ width: `${sendProgress}%` }} />
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800">
        {replyingTo && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Replying to: {replyingTo.user} - {replyingTo.message?.substring(0, 50)}...</p>
            <button onClick={cancelReply} className="text-gray-400 hover:text-gray-600"><FaTimesCircle /></button>
          </div>
        )}
        {selectedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 py-2 bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-dark-800 rounded-lg text-sm">
                <FaFile className="text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">{truncateFileName(file.name)}</span>
                <button onClick={() => removeSelectedFile(index)} className="text-red-500 hover:text-red-600"><FaTimesCircle /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2 p-3">
          <label className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg cursor-pointer transition-colors">
            <FaPaperclip />
            <input type="file" onChange={handleFileChange} ref={fileInputRef} className="hidden" multiple />
          </label>
          <textarea ref={textareaRef} value={newMessage} onChange={handleTextareaChange} onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMessageSend(); }}} placeholder="Type a message..." rows="1" className="flex-1 px-4 py-2 bg-gray-100 dark:bg-dark-700 border-0 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 resize-none" style={{ maxHeight: '120px' }} />
          <button onClick={handleMessageSend} disabled={isSending || (!newMessage.trim() && selectedFiles.length === 0)} className="p-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><IoMdSend className="w-5 h-5" /></button>
        </div>
      </div>
      
      {/* Resize handle */}
      <div 
        onMouseDown={handleMouseDown} 
        className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize hover:bg-primary-500/20 rounded-br-lg transition-colors"
        style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.3) 50%)' }}
      />
      
      {/* Chat History Modal */}
      <ChatHistory 
        userId={user._id}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />
    </div>
  );
};

export default AdminChatbox;